import { dialog } from "nq-component";
import BaseListPresenter from "../../../../base/BaseListPresenter";
import ExportCSVFunction from "../../../../components/ExportCSVFunction";
import { aggregateUseCase } from "../../../../usecases/object";
import {
  GENERAL_PAYROLL_FIELDS,
  GSIS_LOAN_REPORT_FIELDS,
  HDMF_LOAN_REPORT_FIELDS,
  HDMF_PREMIUM_REMITTANCE_FIELDS,
  getReportFields,
  getReportIncludes,
  getReportQueryKeys,
} from "./payrollReportConfig";
import {
  getCoopDeductionByEmployeePipeline,
  getLoanDeductionByEmployeePipeline,
} from "./pipelines/deductions";
import { getPayrollReportExportPipeline } from "./pipelines/exportPipeline";
import { getGeneralPayrollByEmployeePipeline } from "./pipelines/generalPayroll";
import {
  getGsisEmployeeShareByEmployeePipeline,
  getGsisLoansByEmployeePipeline,
} from "./pipelines/gsis";
import {
  getHdmfEmployeeShareByEmployeePipeline,
  getHdmfLoansByEmployeePipeline,
} from "./pipelines/hdmf";
import { getOverpaymentByEmployeePipeline } from "./pipelines/overpayment";
import { getPhicEmployeeShareByEmployeePipeline } from "./pipelines/philhealth";
import { getTaxByEmployeePipeline } from "./pipelines/tax";
import { chunk, withDerivedFields } from "./payrollReportUtils";

export default class PayrollReportsPresenter extends BaseListPresenter {
  init() {
    this.limit = 10;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.report = null;
    this.applyReportQuery();
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.resetDerivedCaches();
    this.reset();
  }

  resetDerivedCaches() {
    this.latestSalaryRateByCode = new Map();
    this.overpaymentByEmployee = new Map();
    this.loanDeductionByEmployee = new Map();
    this.coopDeductionByEmployee = new Map();
    this.generalPayrollByEmployee = new Map();
    this.gsisEmployeeShareByEmployee = new Map();
    this.gsisLoansByEmployee = new Map();
    this.hdmfEmployeeShareByEmployee = new Map();
    this.hdmfLoansByEmployee = new Map();
    this.phicEmployeeShareByEmployee = new Map();
    this.taxByEmployee = new Map();
  }

  applyReportQuery() {
    this.include = getReportIncludes(this.report);
    this.keys = getReportQueryKeys(this.report);
  }

  getReportFields() {
    return getReportFields(this.report);
  }

  setReportType(report) {
    this.report = report || null;
    this.applyReportQuery();
    // Clear derived caches so switching reports recomputes with the right fields.
    this.resetDerivedCaches();
    localStorage.removeItem("currentPage");
    this.current = 1;
    this.view.setState({ selectedReportType: this.report, current: 1 }, () => {
      this.getObjects();
    });
  }

  getEmployeeQuery({ paginated = true } = {}) {
    const query = {
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
      keys: this.keys,
      sort: this.sort,
    };

    if (paginated) {
      query.limit = this.limit;
      query.skip = (this.current - 1) * this.limit;
    }

    return query;
  }

  getExportWhere() {
    return { ...this.where, ...this.search, ...this.filter };
  }

  async getLatestSalaryRates(objects = []) {
    const missingCodes = [
      ...new Set(
        objects
          .filter((object) => !object?.nonRegPosition && object?.salaryCode)
          .map((object) => object.salaryCode)
          .filter((code) => !this.latestSalaryRateByCode.has(code)),
      ),
    ];

    if (missingCodes.length === 0) return this.latestSalaryRateByCode;

    const rows = (
      await Promise.all(
        chunk(missingCodes).map((salaryCodes) =>
          this.findObjectUseCase.execute("salary_grade", {
            where: {
              salaryCode: { $in: salaryCodes },
              status: "active",
            },
            sort: { createdAt: -1 },
            keys: ["salaryCode", "Monthly Rate"],
          }),
        ),
      )
    ).flat();

    const resolvedCodes = new Set();
    rows.forEach((row) => {
      const salaryCode = row?.salaryCode;
      if (!salaryCode || resolvedCodes.has(salaryCode)) return;
      this.latestSalaryRateByCode.set(salaryCode, row?.["Monthly Rate"] ?? "");
      resolvedCodes.add(salaryCode);
    });

    missingCodes.forEach((salaryCode) => {
      if (!this.latestSalaryRateByCode.has(salaryCode)) {
        this.latestSalaryRateByCode.set(salaryCode, "");
      }
    });

    return this.latestSalaryRateByCode;
  }

  /**
   * Shared fetch/cache for per-employee payroll totals.
   * Pass valueKey for a single amount; omit it to cache the whole aggregate row.
   */
  async getPayrollAmountsByEmployee({
    objects = [],
    cache,
    getPipeline,
    valueKey,
    emptyValue = 0,
  }) {
    const missingIds = [
      ...new Set(
        objects
          .map((object) => object?.id)
          .filter((id) => id && !cache.has(id)),
      ),
    ];

    if (missingIds.length === 0) return cache;

    const rows = (
      await Promise.all(
        chunk(missingIds).map((employeeIds) =>
          aggregateUseCase().execute("payroll", getPipeline(employeeIds)),
        ),
      )
    ).flat();

    rows.forEach((row) => {
      if (!row?.employeeId) return;
      cache.set(
        row.employeeId,
        valueKey ? row[valueKey] ?? emptyValue : row,
      );
    });

    // Employees without payroll records still need a resolved value.
    missingIds.forEach((id) => {
      if (!cache.has(id)) cache.set(id, emptyValue);
    });

    return cache;
  }

  getOverpaymentsByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.overpaymentByEmployee,
      getPipeline: getOverpaymentByEmployeePipeline,
      valueKey: "overPayment",
    });
  }

  getLoanDeductionsByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.loanDeductionByEmployee,
      getPipeline: getLoanDeductionByEmployeePipeline,
      valueKey: "totalLoanDeduction",
    });
  }

  getCoopDeductionsByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.coopDeductionByEmployee,
      getPipeline: getCoopDeductionByEmployeePipeline,
      valueKey: "totalCoopDeduction",
    });
  }

  getTaxByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.taxByEmployee,
      getPipeline: getTaxByEmployeePipeline,
      valueKey: "tax",
    });
  }

  getGeneralPayrollByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.generalPayrollByEmployee,
      getPipeline: getGeneralPayrollByEmployeePipeline,
      emptyValue: {},
    });
  }

  getGsisEmployeeShareByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.gsisEmployeeShareByEmployee,
      getPipeline: getGsisEmployeeShareByEmployeePipeline,
      valueKey: "employeeShare",
    });
  }

  getGsisLoansByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.gsisLoansByEmployee,
      getPipeline: getGsisLoansByEmployeePipeline,
      emptyValue: {},
    });
  }

  getHdmfEmployeeShareByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.hdmfEmployeeShareByEmployee,
      getPipeline: getHdmfEmployeeShareByEmployeePipeline,
      valueKey: "employeeContribution",
    });
  }

  getPhicEmployeeShareByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.phicEmployeeShareByEmployee,
      getPipeline: getPhicEmployeeShareByEmployeePipeline,
      valueKey: "employeeShare",
    });
  }

  getHdmfLoansByEmployee(objects = []) {
    return this.getPayrollAmountsByEmployee({
      objects,
      cache: this.hdmfLoansByEmployee,
      getPipeline: getHdmfLoansByEmployeePipeline,
      emptyValue: {},
    });
  }

  async getAllGsisLoansByEmployee() {
    const rows =
      (await aggregateUseCase().execute(
        "payroll",
        getGsisLoansByEmployeePipeline(),
      )) || [];

    rows.forEach((row) => {
      if (!row?.employeeId) return;
      this.gsisLoansByEmployee.set(row.employeeId, row);
    });

    return this.gsisLoansByEmployee;
  }

  async getAllHdmfLoansByEmployee() {
    const rows =
      (await aggregateUseCase().execute(
        "payroll",
        getHdmfLoansByEmployeePipeline(),
      )) || [];

    rows.forEach((row) => {
      if (!row?.employeeId) return;
      this.hdmfLoansByEmployee.set(row.employeeId, row);
    });

    return this.hdmfLoansByEmployee;
  }

  async prepareObjects(objects = []) {
    const fields = this.getReportFields();
    const needsMonthlyRate =
      fields.includes("monthlyRate") || fields.includes("longevityPay");
    const remittanceKind =
      this.report?.key === "gsis_remittance" ||
      this.report?.key === "gsis_premium_remittance"
        ? "gsis"
        : this.report?.key === "philhealth_premium_remittance"
          ? "philhealth"
          : this.report?.key === "phic_premium_remittance"
            ? "phic"
            : null;
    const paymentSummaryKind =
      this.report?.key === "gsis_payment_summary"
        ? "gsis"
        : this.report?.key === "hdmf_payment_summary"
          ? "hdmf"
          : null;
    const needsHdmfPremiumRemittance = HDMF_PREMIUM_REMITTANCE_FIELDS.some(
      (field) => fields.includes(field),
    );
    const needsHdmfLoans =
      HDMF_LOAN_REPORT_FIELDS.some((field) => fields.includes(field)) ||
      paymentSummaryKind === "hdmf";

    const [
      latestRatesByCode,
      overpaymentByEmployee,
      loanDeductionByEmployee,
      coopDeductionByEmployee,
      generalPayrollByEmployee,
      gsisEmployeeShareByEmployee,
      gsisLoansByEmployee,
      hdmfEmployeeShareByEmployee,
      hdmfLoansByEmployee,
      phicEmployeeShareByEmployee,
      taxByEmployee,
    ] = await Promise.all([
      needsMonthlyRate ? this.getLatestSalaryRates(objects) : new Map(),
      fields.includes("overPayment")
        ? this.getOverpaymentsByEmployee(objects)
        : new Map(),
      fields.includes("totalLoanDeduction") || paymentSummaryKind === "gsis"
        ? this.getLoanDeductionsByEmployee(objects)
        : new Map(),
      fields.includes("totalCoopDeduction")
        ? this.getCoopDeductionsByEmployee(objects)
        : new Map(),
      GENERAL_PAYROLL_FIELDS.some(
        (field) => field !== "tax" && fields.includes(field),
      )
        ? this.getGeneralPayrollByEmployee(objects)
        : new Map(),
      remittanceKind === "gsis" || paymentSummaryKind === "gsis"
        ? this.getGsisEmployeeShareByEmployee(objects)
        : new Map(),
      GSIS_LOAN_REPORT_FIELDS.some((field) => fields.includes(field))
        ? this.getGsisLoansByEmployee(objects)
        : new Map(),
      needsHdmfPremiumRemittance || paymentSummaryKind === "hdmf"
        ? this.getHdmfEmployeeShareByEmployee(objects)
        : new Map(),
      needsHdmfLoans ? this.getHdmfLoansByEmployee(objects) : new Map(),
      remittanceKind === "philhealth" || remittanceKind === "phic"
        ? this.getPhicEmployeeShareByEmployee(objects)
        : new Map(),
      fields.includes("tax") &&
      !GENERAL_PAYROLL_FIELDS.some(
        (field) => field !== "tax" && fields.includes(field),
      )
        ? this.getTaxByEmployee(objects)
        : new Map(),
    ]);

    return withDerivedFields(objects, fields, {
      latestRatesByCode,
      overpaymentByEmployee,
      loanDeductionByEmployee,
      coopDeductionByEmployee,
      generalPayrollByEmployee,
      gsisEmployeeShareByEmployee,
      gsisLoansByEmployee,
      hdmfEmployeeShareByEmployee,
      hdmfLoansByEmployee,
      phicEmployeeShareByEmployee,
      taxByEmployee,
      remittanceKind,
      paymentSummaryKind,
    });
  }

  hasFullyLoadedResults() {
    const total = this.count || this.view.state?.count || 0;
    return total > 0 && this.objects?.length === total;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(
        collection,
        this.getEmployeeQuery({ paginated: true }),
      );
      const mappedObjects = await this.prepareObjects(objects);
      this.objects = mappedObjects;
      this.view.setObjects(mappedObjects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  setEmployeeFilter(employee) {
    const selected =
      employee && typeof employee === "object" && employee.id ? employee : null;

    localStorage.removeItem("currentPage");
    this.current = 1;
    this.filter = selected?.id ? { id: selected.id } : {};
    this.view.setState({ employee: selected, current: 1 }, () => {
      this.getObjects();
    });
  }

  async fetchExportObjects() {
    if (this.hasFullyLoadedResults()) {
      return this.objects;
    }

    const fields = this.getReportFields();
    const remittanceKind =
      this.report?.key === "gsis_remittance" ||
      this.report?.key === "gsis_premium_remittance"
        ? "gsis"
        : this.report?.key === "philhealth_premium_remittance"
          ? "philhealth"
          : this.report?.key === "phic_premium_remittance"
            ? "phic"
            : null;
    const paymentSummaryKind =
      this.report?.key === "gsis_payment_summary"
        ? "gsis"
        : this.report?.key === "hdmf_payment_summary"
          ? "hdmf"
          : null;
    const needsGsisLoans = GSIS_LOAN_REPORT_FIELDS.some((field) =>
      fields.includes(field),
    );
    const needsHdmfLoans = HDMF_LOAN_REPORT_FIELDS.some((field) =>
      fields.includes(field),
    );

    const [objects, gsisLoansByEmployee, hdmfLoansByEmployee] =
      await Promise.all([
        aggregateUseCase().execute(
          this.view.getCollectionName(),
          getPayrollReportExportPipeline(this.getExportWhere(), fields, {
            remittanceKind,
            paymentSummaryKind,
          }),
        ),
        needsGsisLoans ? this.getAllGsisLoansByEmployee() : new Map(),
        needsHdmfLoans ? this.getAllHdmfLoansByEmployee() : new Map(),
      ]);

    // Pipeline resolves money fields; longevity still needs JS.
    // Don't re-resolve mapped money fields from empty caches.
    return withDerivedFields(objects || [], fields, {
      gsisLoansByEmployee,
      hdmfLoansByEmployee,
      remittanceKind,
      paymentSummaryKind,
      resolveMonthlyRate: false,
      resolveOverPayment: false,
      resolveLoanDeduction: false,
      resolveCoopDeduction: false,
      resolveGeneralPayroll: false,
      resolveRemittance: false,
      resolveGsisLoans: needsGsisLoans,
      resolvePaymentSummary: false,
      resolveHdmfPremiumRemittance: false,
      resolveHdmfLoans: needsHdmfLoans,
      resolveTax: false,
    });
  }

  async exportToCSV() {
    const schema =
      this.view.getTableSchema?.() ||
      this.view.getSchema(this.view.getCollectionName());
    if (!schema) {
      this.view.showError("Payroll schema not found.");
      return;
    }

    this.view.setState({ isExporting: true });
    try {
      const exportObjects = await this.fetchExportObjects();

      if (!exportObjects?.length) {
        this.view.showError("No payroll data found for export.");
        return;
      }

      ExportCSVFunction(
        exportObjects,
        this.report?.label || "Payroll Reports",
        schema,
        this.getReportFields(),
      );
      this.view.showSuccess("Export completed");
    } catch (error) {
      this.view.showError(error?.message || error);
    } finally {
      this.view.setState({ isExporting: false });
      dialog.close();
    }
  }
}
